import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { useDealValidation, type ValidationIssue } from '@/hooks/useDealValidation';

interface DealClosingValidationProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  onConfirm: () => void;
  onNavigateToItem?: (itemId: string, issue: ValidationIssue['issue']) => void;
}

const ISSUE_ICONS: Record<ValidationIssue['issue'], React.ReactNode> = {
  missing_applicant: <XCircle className="h-5 w-5 text-destructive" />,
  missing_payer: <XCircle className="h-5 w-5 text-destructive" />,
  missing_poc: <XCircle className="h-5 w-5 text-destructive" />,
  missing_eligibility: <AlertTriangle className="h-5 w-5 text-amber-500" />,
};

const ISSUE_LABELS: Record<ValidationIssue['issue'], string> = {
  missing_applicant: 'Applicant',
  missing_payer: 'Payer',
  missing_poc: 'Point of Contact',
  missing_eligibility: 'Elegibilidade',
};

export default function DealClosingValidation({
  isOpen,
  onClose,
  dealId,
  onConfirm,
  onNavigateToItem,
}: DealClosingValidationProps) {
  const { t } = useTranslation();
  const { issues, isLoading, canClose, errorCount, revalidate } = useDealValidation(dealId);

  // Revalidar sempre que o modal abrir para garantir dados frescos
  useEffect(() => {
    if (isOpen) {
      revalidate();
    }
  }, [isOpen, revalidate]);

  // Agrupar issues por item
  const groupedIssues = issues.reduce((acc, issue) => {
    if (!acc[issue.item_id]) {
      acc[issue.item_id] = {
        item_name: issue.item_name,
        issues: [],
      };
    }
    acc[issue.item_id].issues.push(issue);
    return acc;
  }, {} as Record<string, { item_name: string; issues: ValidationIssue[] }>);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {canClose ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Pronto para Fechar
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Pendências para Fechar
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Validando...
            </div>
          ) : canClose ? (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-medium">Tudo certo!</p>
              <p className="text-muted-foreground">
                Todos os itens têm Applicant, Payer e Elegibilidade definidos.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Badge variant="destructive">{errorCount} pendência(s)</Badge>
                <span className="text-sm text-muted-foreground">
                  Resolva as pendências abaixo antes de fechar o deal
                </span>
              </div>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {Object.entries(groupedIssues).map(([itemId, { item_name, issues: itemIssues }]) => (
                  <div
                    key={itemId}
                    className="p-3 border rounded-lg space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{item_name}</span>
                       {onNavigateToItem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const firstIssue = itemIssues[0]?.issue;
                            onClose();
                            onNavigateToItem(itemId, firstIssue);
                          }}
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Ir para item
                        </Button>
                      )}
                    </div>

                    <div className="space-y-1">
                      {itemIssues.map((issue, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-sm"
                        >
                          {ISSUE_ICONS[issue.issue]}
                          <span>{issue.message}</span>
                          <Badge variant="outline" className="text-xs">
                            {ISSUE_LABELS[issue.issue]}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canClose}
            variant={canClose ? 'default' : 'secondary'}
          >
            {canClose ? 'Confirmar Fechamento' : 'Não é possível fechar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
